import { getBackendUrl } from "@/lib/backend";
import { useCallback, useEffect, useState } from "react";
import axios from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, ToggleRight, ToggleLeft, Loader2, GraduationCap, Percent, Sparkles } from "lucide-react";

const API = `${getBackendUrl()}/api`;

const TYPES = [
  { id: "bonus_credits", label: "Bonus credits", icon: Sparkles,
    hint: "Adds N credits to the redeemer's wallet." },
  { id: "edu_grant", label: "Edu plan grant", icon: GraduationCap,
    hint: "Unlocks Facetra Edu (CAD-restricted) for N months." },
  { id: "percent_off", label: "Percent off (Stripe)", icon: Percent,
    hint: "Validates as a Stripe discount code at checkout." },
];

function typeIcon(t) {
  const cfg = TYPES.find((x) => x.id === t);
  return cfg ? cfg : { label: t, icon: Ticket };
}

function fmtValue(c) {
  if (c.type === "bonus_credits") return `+${c.value} credits`;
  if (c.type === "edu_grant") return `Edu · ${c.value} mo`;
  if (c.type === "percent_off") return `${c.value}% off`;
  return c.value;
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [code, setCode] = useState("");
  const [type, setType] = useState("bonus_credits");
  const [value, setValue] = useState("");
  const [desc, setDesc] = useState("");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/coupons`);
      setCoupons(r.data?.coupons || []);
    } catch (err) {
      console.error("Load coupons failed", err);
      toast.error("Could not load coupons.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const cleanCode = code.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    if (!cleanCode) return toast.error("Enter a code.");
    if (!value || Number(value) <= 0) return toast.error("Enter a positive value.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: cleanCode,
        type,
        value: Number(value),
        description: desc.trim() || undefined,
        max_redemptions: maxRedemptions ? Number(maxRedemptions) : undefined,
      });
      toast.success(`Created ${cleanCode}`);
      setCode(""); setValue(""); setDesc(""); setMaxRedemptions("");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Create failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/coupons/${c.id}`, { active: !c.active });
      load();
    } catch (err) {
      console.error("Toggle coupon failed", err);
      toast.error("Toggle failed.");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      console.error("Delete coupon failed", err);
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <Ticket className="w-3 h-3" /> Promo · Coupons
          </div>
          <h2 className="font-display text-2xl text-white mt-1">Coupon codes</h2>
          <p className="text-[12px] text-white/55 mt-0.5">
            Hand out bonus credits, unlock Edu access for institutions, or stage Stripe percent-off codes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          data-testid="coupons-create-toggle"
          className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#1A1814] transition"
        >
          <Plus className="w-3.5 h-3.5" /> New coupon
        </button>
      </div>

      {creating && (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Code</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                placeholder="NIFT2026"
                maxLength={40}
                data-testid="coupon-new-code"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white tracking-[0.12em] uppercase placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                data-testid="coupon-new-type"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              >
                {TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-white/40 mt-1">{TYPES.find((t) => t.id === type)?.hint}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                {type === "bonus_credits" ? "Credits" : type === "edu_grant" ? "Months" : "% off"}
              </label>
              <input
                type="number"
                min="1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="coupon-new-value"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                Max redemptions
              </label>
              <input
                type="number"
                min="1"
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder="(unlimited)"
                data-testid="coupon-new-max"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Description</label>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="e.g. NIFT Delhi 2026 cohort"
                maxLength={200}
                data-testid="coupon-new-desc"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={create}
              disabled={submitting || !code || !value}
              data-testid="coupon-new-submit"
              className="text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create coupon
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-10 text-center text-white/55 text-sm">
          Loading coupons…
        </div>
      ) : coupons.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#0F0F0F] p-12 text-center">
          <Ticket className="w-7 h-7 text-[#D4AF37]/60 mx-auto mb-2" />
          <p className="text-sm text-white/65">No coupons yet.</p>
          <p className="text-[11px] text-white/40 mt-1">Create one to start handing out free credits or Edu access.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#141414] border-b border-white/8 text-[10px] uppercase tracking-[0.18em] text-white/45">
              <tr>
                <th className="text-left px-4 py-2.5">Code</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Value</th>
                <th className="text-left px-4 py-2.5">Redemptions</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => {
                const Icon = typeIcon(c.type).icon;
                const cap = c.max_redemptions || "∞";
                return (
                  <tr
                    key={c.id}
                    data-testid={`coupon-row-${c.code}`}
                    className={`border-b border-white/5 hover:bg-white/[0.02] transition ${c.active ? "" : "opacity-50"}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-[#D4AF37] tracking-wide">{c.code}</td>
                    <td className="px-4 py-2.5 text-white/75 inline-flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5 text-white/40" /> {typeIcon(c.type).label}
                    </td>
                    <td className="px-4 py-2.5 text-white/75">{fmtValue(c)}</td>
                    <td className="px-4 py-2.5 text-white/55 text-[12px]">
                      {c.redemption_count || 0} / {cap}
                    </td>
                    <td className="px-4 py-2.5 text-white/55 text-[12px]">{c.description || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => toggleActive(c)}
                          data-testid={`coupon-toggle-${c.code}`}
                          className="p-1.5 rounded-md text-white/55 hover:text-[#D4AF37] hover:bg-white/5 transition"
                          title={c.active ? "Deactivate" : "Activate"}
                        >
                          {c.active ? <ToggleRight className="w-4 h-4 text-[#D4AF37]" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(c)}
                          data-testid={`coupon-delete-${c.code}`}
                          className="p-1.5 rounded-md text-white/40 hover:text-red-400 hover:bg-white/5 transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
